import {ApDynformsConfigFieldset}                 from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynGridPropertyColumnConfig}            from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {ApDynformsConfigNumerictextbox}           from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {distinctUntilChanged, filter, map}        from 'rxjs/operators';
import {ApDynformsValidator}                      from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                               from '@angular/forms';
import {Observable}                               from 'rxjs';
import {Injectable, TemplateRef}                  from '@angular/core';
import {ApDynGridGroupColumnConfig}               from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {ApDynGridColumnConfigBase}                from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {GetRoundNumericPipe}                      from '../../../ap-utils';
import {NFertilizationCommonEntryService}         from '../services/n-fertilization-common-entry.service';
import {NAppSettingsConfigBase}                   from './base/n-app-settings-config.base';
import IApplicationSettings = Data.Agronomic.IApplicationSettings;

@Injectable()
export class NFertilizationConfig extends NAppSettingsConfigBase {
  constructor(private nFertilizationCommonEntryService: NFertilizationCommonEntryService,
              private roundNumericPipe: GetRoundNumericPipe) {
    super();
  }

  public formBuilder(values: any, formValues$: Observable<any>, applicationSettings: IApplicationSettings,
                     cropKey: number | undefined, avgNMin: number | undefined, isModalField: boolean,
                     applyBlinkEffectOnInit: boolean, diagramTemplateRef?: TemplateRef<any>): ApDynformsConfigFieldset[] {
    const applyBlinkEffect = !(isModalField || !applyBlinkEffectOnInit);
    const parameters = applicationSettings.Parameter;
    const min$ = formValues$.pipe(
      filter(x => Number.isFinite(x['Minimum']) && x['Minimum'] >= 0),
      map(v => v['Minimum'])
    );
    const max$ = formValues$.pipe(
      filter(x => Number.isFinite(x['Maximum']) && x['Maximum'] >= 0),
      map(v => v['Maximum'])
    );
    return [
      new ApDynformsConfigFieldset({
        key: 'Agronomy',
        legend: 'Global__Agronomy',
        before: diagramTemplateRef,
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'Threshold',
            label: 'Global__Threshold_SN',
            value: values['Threshold'],
            value$: formValues$.pipe(
              filter(() => !isModalField),
              filter(formValues => Number.isFinite(formValues['ECStage'])),
              distinctUntilChanged((prev, curr) => prev['ECStage'] === curr['ECStage']),
              map((formValues: any) => this.nFertilizationCommonEntryService.getThreshold(formValues['ECStage'], cropKey, avgNMin))
            ),
            min: 0,
            max: 100,
            step: 1,
            decimals: 0,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          this.generateSlopeControls(parameters, values, !(isModalField || !applyBlinkEffectOnInit))
        ]
      }),
      new ApDynformsConfigFieldset({
        legend: 'Nutrients_Pages_Popups__Amount',
        key: 'Amount',
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'Amount',
            label: 'NFertilisation_calibration_recommendation_kg_N_per_ha',
            value: values['Amount'],
            infoText: 'NFertilisation_calibration_recommendation_InfoText',
            min: 0,
            max: max$,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Constant',
            label: 'Nutrients__Constant_Amount_kg_N_per_ha',
            value: values['Constant'],
            min: min$,
            max: max$,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Minimum',
            label: 'Nutrients__Minimum_kg_n_per_ha',
            infoText: 'NFertilizationMinimumAdjustmentInfoText',
            value: values['Minimum'],
            value$: min$,
            min: 0,
            max: max$,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Maximum',
            label: 'Nutrients__Maximum_kg_n_per_ha',
            infoText: 'NFertilizationMaximumAdjustmentInfoText',
            value: values['Maximum'],
            value$: max$,
            min: min$,
            max: 250,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          })
        ]
      })
    ];
  }

  public gridBuilderConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Control_Function',
        field: 'ControlFactorDescription',
        hide: true,
        width: 50
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__KgNPerHa',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'NFertilisation_calibration_recommendation_kg_N_per_ha',
            field: 'Amount',
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Threshold',
            field: 'Threshold',
            hide: false,
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__RateConst',
            field: 'Constant',
            hide: true,
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: 'Minimum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: 'Maximum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          })
        ]
      })
    ];
  }

  public updateFormValuesByParameters(values: any, cropKey: number | undefined, avgNMin: number | undefined,
                                      applicationSettings: IApplicationSettings | undefined): void {
    const parameters = applicationSettings.Parameter;
    if (!Number.isFinite(values['Threshold'])) {
      values['Threshold'] = this.nFertilizationCommonEntryService.getThreshold(values['ECStage'], cropKey, avgNMin);
    }
    if (!Number.isFinite(values['Amount'])) {
      values['Amount'] = parameters?.targetValue ?? parameters.recommendation;
    }
    if (!Number.isFinite(values['Constant'])) {
      values['Constant'] = parameters?.uniform;
    }
    if (!Number.isFinite(values['Minimum'])) {
      values['Minimum'] = parameters?.min;
    }
    if (!Number.isFinite(values['Maximum'])) {
      values['Maximum'] = parameters?.max;
    }
    if (!Number.isFinite(values['ControlFactor'])) {
      values['ControlFactor'] = parameters?.slope?.FirstOrDefault()?.Slope ?? 1;
    }
  }
}
